import React, { useCallback, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Card, Typography, Button, Alert, useTheme, useMediaQuery, Divider } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import { DataGrid } from "@mui/x-data-grid";
import {
    DATA_GRID_SX,
    DATA_GRID_SLOT_PROPS,
    DATA_GRID_LOCALE_TEXT,
    DATA_GRID_INITIAL_STATE,
    DATA_GRID_PAGE_SIZE_OPTIONS,
} from "../../../components/ui/dataGridStyles";

import { useInventarios, useDrawerInventario, useRevisarInventario } from "../hooks";
import { generateInventarioColumns } from "../helpers/columns";
import { InventariosMobileList } from "../components/lists";
import { ModalEliminarInventario, ModalRevisarInventario } from "../components/modals";
import { DrawerDetalleInventario } from "../components/drawers";
import { EstadisticasInventarios } from "../components/ui";
import { FiltrosInventario } from "../components/forms";
import { INVENTARIO_ESTADO } from "../constants";
import { BOTON_PRIMARIO_SX } from "../../../components/ui/buttonStyles";
import { StateContext } from "../../../components/context/StateProvider";
import Toasts from "../../../components/notificacion/Toasts";

// Mensaje de lista vacía compartido entre el DataGrid (desktop) y
// InventariosMobileList (móvil), para no duplicar el literal
const MENSAJE_SIN_INVENTARIOS = "No hay inventarios creados todavía";

// slots.noRowsOverlay del DataGrid: por defecto pinta "No hay filas" (esES),
// que no distingue "no hay datos" de "el filtro no devuelve nada". Se
// sustituye por el mismo mensaje que la vista móvil.
const NoRowsOverlay = () => (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", p: 3 }}>
        <Typography sx={{ color: "#6c757d", fontSize: 14 }}>{MENSAJE_SIN_INVENTARIOS}</Typography>
    </Box>
);

/**
 * Página de listado de inventarios (admin): DataGrid en escritorio, tarjetas
 * en móvil. useInventarios aporta los datos y las mutaciones,
 * helpers/columns.js las columnas de escritorio, InventariosMobileList la
 * vista móvil y ModalEliminarInventario la confirmación de borrado.
 *
 * Control de acceso: el rol ADMIN requerido para esta página se resuelve en
 * src/routes.js (roles: [Roles.ADMIN.role]), NO aquí (ver CLAUDE.md, Estados
 * de Carga, regla 3).
 */
const ListaInventarios = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
    const navigate = useNavigate();
    const { state, updateState, refrescarContadorInventarios } = useContext(StateContext);

    const { inventarios, estadisticas, filtros, aplicarFiltros, loading, error, eliminar, recargar } = useInventarios();
    const drawer = useDrawerInventario();

    const [modalEliminar, setModalEliminar] = useState({ open: false, inventario: null });
    const [eliminando, setEliminando] = useState(false);

    const handleAbrirCrear = useCallback(() => navigate("/inventarios/nuevo"), [navigate]);

    const { abrir: abrirDrawer, setInventario: setInventarioDrawer } = drawer;
    const handleVer = useCallback((fila) => abrirDrawer(fila.id), [abrirDrawer]);

    const handleRevisado = useCallback((actualizado) => {
        setInventarioDrawer(actualizado);
        recargar();
    }, [setInventarioDrawer, recargar]);
    const revision = useRevisarInventario({ inventario: drawer.inventario, onRevisado: handleRevisado });

    const handleEditar = useCallback(
        (fila) => navigate(`/inventarios/${fila.id}/editar`),
        [navigate]
    );

    const handleEliminar = useCallback((fila) => setModalEliminar({ open: true, inventario: fila }), []);

    const handleCerrarModalEliminar = useCallback(() => {
        if (!eliminando) setModalEliminar({ open: false, inventario: null });
    }, [eliminando]);

    const handleConfirmarEliminar = useCallback(async () => {
        setEliminando(true);
        try {
            await eliminar(modalEliminar.inventario.id);
            refrescarContadorInventarios?.();
            updateState({
                toasts: [...state.toasts, Toasts.generateToast("Inventario eliminado correctamente", "success")],
            });
            setModalEliminar({ open: false, inventario: null });
        } catch (err) {
            updateState({
                toasts: [...state.toasts, Toasts.generateToast(err.message || "No se pudo eliminar el inventario.", "error")],
            });
        } finally {
            setEliminando(false);
        }
    }, [eliminar, modalEliminar.inventario, refrescarContadorInventarios, state.toasts, updateState]);

    const columns = useMemo(
        () => generateInventarioColumns({ onVer: handleVer, onEditar: handleEditar, onEliminar: handleEliminar }),
        [handleVer, handleEditar, handleEliminar]
    );

    return (
        <Box sx={{ width: "100%" }}>
            <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)", borderRadius: 2 }}>
                {/* Header */}
                <Box sx={{ p: 3, pb: 2 }}>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            justifyContent: "space-between",
                            alignItems: { xs: "stretch", sm: "flex-start" },
                            gap: 2,
                            mb: 0.5,
                        }}
                    >
                        <Box>
                            <Typography
                                variant={isMobile ? "h6" : "h5"}
                                sx={{ fontWeight: 700, color: "#212529", mb: 0.5, display: "flex", alignItems: "center", gap: 1 }}
                            >
                                <FactCheckIcon sx={{ fontSize: isMobile ? 24 : 28 }} />
                                Inventarios
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#6c757d" }}>
                                Crea, asigna y consulta las listas de conteo de inventario
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAbrirCrear}
                            disabled={modalEliminar.open}
                            fullWidth={isMobile}
                            sx={BOTON_PRIMARIO_SX}
                        >
                            Nueva lista inventario
                        </Button>
                    </Box>

                    {estadisticas && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <EstadisticasInventarios
                                estadisticas={estadisticas}
                                estadoActivo={filtros.estado}
                                onSeleccionar={(estado) => aplicarFiltros({ ...filtros, estado })}
                            />
                        </>
                    )}

                    <Divider sx={{ my: 2 }} />
                    <FiltrosInventario filtros={filtros} onAplicar={aplicarFiltros} loading={loading} totalResultados={inventarios.length} />
                </Box>

                {/* Contenido */}
                <Box sx={{ px: isMobile ? 2 : 3, pb: 3 }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {!isMobile ? (
                        <DataGrid
                            rows={inventarios}
                            columns={columns}
                            localeText={DATA_GRID_LOCALE_TEXT}
                            autoHeight
                            loading={loading}
                            disableRowSelectionOnClick
                            disableColumnMenu
                            initialState={DATA_GRID_INITIAL_STATE}
                            pageSizeOptions={DATA_GRID_PAGE_SIZE_OPTIONS}
                            slots={{ noRowsOverlay: NoRowsOverlay }}
                            slotProps={DATA_GRID_SLOT_PROPS}
                            sx={DATA_GRID_SX}
                        />
                    ) : (
                        <InventariosMobileList
                            rows={inventarios}
                            loading={loading}
                            onVer={handleVer}
                            onEditar={handleEditar}
                            onEliminar={handleEliminar}
                        />
                    )}
                </Box>
            </Card>

            <ModalEliminarInventario
                show={modalEliminar.open}
                onHide={handleCerrarModalEliminar}
                onConfirm={handleConfirmarEliminar}
                inventario={modalEliminar.inventario}
                loading={eliminando}
            />

            <DrawerDetalleInventario
                open={drawer.abierto}
                onClose={drawer.cerrar}
                inventario={drawer.inventario}
                loading={drawer.loading}
                error={drawer.error}
                onRevisar={
                    drawer.inventario?.estado === INVENTARIO_ESTADO.COMPLETADO ? revision.abrir : undefined
                }
            />

            <ModalRevisarInventario
                show={revision.abierto}
                onHide={revision.cerrar}
                onConfirm={revision.confirmar}
                inventario={drawer.inventario}
                loading={revision.revisando}
            />
        </Box>
    );
};

export default ListaInventarios;
